import { ReactNode, useEffect } from 'react';
import { useNavigate, Navigate } from 'react-router-dom';
import { useAuthStore } from '../../stores/authStore';
import { LoginForm } from './LoginForm';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { ShieldAlert, ArrowLeft } from "lucide-react";

interface ProtectedRouteProps {
  children: ReactNode;
  requiredRoles?: string[];
}

export function ProtectedRoute({ children, requiredRoles }: ProtectedRouteProps) {
  const { isAuthenticated, user, checkAuth } = useAuthStore();
  const navigate = useNavigate();

  useEffect(() => {
    // Check auth on mount
    checkAuth();
  }, [checkAuth]);

  // Show login if not authenticated
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  // Check role authorization if required
  if (requiredRoles && user) {
    if (!requiredRoles.includes(user.role)) {
      return (
        <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
          <Card className="w-full max-w-md border-red-200 shadow-lg">
            <CardHeader className="text-center pb-2">
              <div className="mx-auto bg-red-100 p-3 rounded-full w-16 h-16 flex items-center justify-center mb-4">
                <ShieldAlert className="h-8 w-8 text-red-600" />
              </div>
              <CardTitle className="text-2xl font-bold text-slate-900">Access Denied</CardTitle>
              <CardDescription className="text-slate-500">
                You do not have permission to access this page.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="bg-slate-100 p-4 rounded-md text-sm space-y-2 border border-slate-200">
                <div className="flex justify-between">
                  <span className="text-slate-500">Required Role:</span>
                  <span className="font-medium text-slate-900 capitalize">{requiredRoles.join(', ')}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-500">Your Role:</span>
                  <span className="font-medium text-slate-900 capitalize">{user.role}</span>
                </div>
              </div>

              <Button
                onClick={() => navigate(-1)}
                className="w-full"
                variant="outline"
              >
                <ArrowLeft className="mr-2 h-4 w-4" />
                Go Back
              </Button>
            </CardContent>
          </Card>
        </div>
      );
    }
  }

  // Render protected content
  return <>{children}</>;
}
